import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { request } from '@anwb/poncho/utilities/api';
import { setCustomer, setCustomerDataFilledFromLogin } from 'store/actions';
import { customerDataFilledFromLoginSelector, plCustomerSelector } from 'store/selectors';
import { PLCustomerData } from 'types/plCustomerData';
import { getApiKey } from 'utils/setKongConfig';

import { mapRelationApiResponseToPLCustomer } from './mapRelationApiResponseToPLCustomer';

const relationApiV2Endpoints = [
	'/v2/relations/me/personal',
	'/v2/relations/me/contact/emailaddress',
	'/v2/relations/me/contact',
	'/v2/relations/me/membership',
];

export const useLoggedInUserInformation = () => {
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [plCustomerData, setPlCustomerData] = useState<PLCustomerData>();
	const dispatch = useDispatch();

	const isCustomerDataFilledFromLogin = useSelector(customerDataFilledFromLoginSelector);

	const preloadedCustomerData = useSelector(plCustomerSelector);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			const fetchEndpoint = async (endpoint: string): Promise<Response> => {
				try {
					const response = await request(
						{
							url: endpoint,
							options: {
								credentials: 'include',
							},
						},
						{
							clientId: getApiKey(),
						}
					);
					if (response.ok) {
						return response;
					}
					setError(true);
					return Promise.reject(`Non-OK response from ${endpoint}: ${response.status}`);
				} catch (errorResponse) {
					console.error(`Error fetching ${endpoint}:`, errorResponse);
					setError(true);
					return Promise.reject(`Error fetching ${endpoint}.`);
				}
			};

			const promises = relationApiV2Endpoints.map(async (endpoint) => {
				const response = await fetchEndpoint(endpoint);
				const responseJson = await response.json().catch((e) => {
					console.error(`Error parsing json from ${endpoint}`, e);
					setError(true);
					return Promise.reject();
				});
				return responseJson;
			});

			Promise.all(promises)
				.then((dataArray) => {
					const combinedResponse = Object.assign({}, ...dataArray.filter(Boolean));
					const plCustomer = mapRelationApiResponseToPLCustomer(combinedResponse);
					setPlCustomerData(plCustomer);
					dispatch(setCustomer(plCustomer));
					dispatch(setCustomerDataFilledFromLogin(true));
					setLoading(false);
				})
				.catch((e) => {
					console.error(e);
					setLoading(false);
					setError(true);
				});
		};

		if (isCustomerDataFilledFromLogin) {
			setPlCustomerData(preloadedCustomerData);
		} else {
			fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { plCustomerData, loading, error };
};
